import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, CheckCircle, AlertCircle, 
  FileText, Gavel, Award, Video, BookOpen, Users, Scale,
  AlertTriangle, Clock, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import BodyCamPlayer from '@/components/BodyCamPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// YouTube video ID for body cam footage
const BODY_CAM_VIDEO_ID = "6rY1diL6dtg";

const KenHillCaseStudyPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState('story');

  useEffect(() => {
    const fetchCaseStudy = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/quizzes/ken-hill-case-study`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!res.ok) throw new Error('Failed to fetch case study');
        
        const data = await res.json();
        setCaseStudy(data);
      } catch (err) {
        console.error('Failed to fetch case study:', err);
        setError('Failed to load the Ken Hill case study.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCaseStudy();
  }, []);

  const handleAnswer = (questionNumber, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionNumber]: answer
    }));
  };

  const handleSubmit = () => {
    const totalQuestions = caseStudy?.questions?.length || 0;
    const answeredQuestions = Object.keys(answers).length;
    
    if (answeredQuestions < totalQuestions) {
      toast.error(`Please answer all questions (${answeredQuestions}/${totalQuestions} answered)`);
      return;
    }
    
    setSubmitted(true);
    setShowResults(true);
    toast.success('Case study submitted! Review your answers below.');
  };

  const getScore = () => {
    if (!caseStudy?.questions) return { correct: 0, total: 0, percentage: 0 };
    
    let correct = 0;
    caseStudy.questions.forEach(q => {
      if (answers[q.number] === q.correct_answer) {
        correct++;
      }
    });
    
    return {
      correct,
      total: caseStudy.questions.length,
      percentage: Math.round((correct / caseStudy.questions.length) * 100)
    };
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setShowResults(false);
    setActiveTab('questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm border">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const score = getScore();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-4 px-6 shadow-lg sticky top-0 z-50">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Gavel className="w-5 h-5" />
            <span className="font-semibold">State v. Ken Hill Case Study</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto py-8 px-6">
        {/* Title Section */}
        <div className="bg-gradient-to-r from-red-900 to-red-700 rounded-lg p-8 text-white mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">STATE v. KEN HILL</h1>
              <p className="text-red-200">Mesa County Case No. 2022CR599 | GJPD Incident 2022-00008246</p>
            </div>
          </div>
          <p className="text-red-100 text-lg font-medium">
            Authorization, Entry Law, and the Limits of Bail Enforcement Authority in Colorado
          </p>
          <p className="text-red-200 text-sm mt-2">
            Incident Date: February 21, 2022 | Railyard Loop Apartments, Grand Junction, CO
          </p>
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="story" className="flex items-center gap-2" data-testid="tab-story">
              <BookOpen className="w-4 h-4" />
              Full Case Study
            </TabsTrigger>
            <TabsTrigger value="video" className="flex items-center gap-2" data-testid="tab-video">
              <Video className="w-4 h-4" />
              Evidence & Interrogations
            </TabsTrigger>
            <TabsTrigger value="investigation" className="flex items-center gap-2" data-testid="tab-investigation">
              <Gavel className="w-4 h-4" />
              Investigation
            </TabsTrigger>
            <TabsTrigger value="questions" className="flex items-center gap-2" data-testid="tab-questions">
              <FileText className="w-4 h-4" />
              Assessment Questions
            </TabsTrigger>
          </TabsList>

          {/* FULL CASE STUDY TAB */}
          <TabsContent value="story" className="space-y-6">
            {/* Where You Stand Now */}
            <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="section-where-you-stand">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#1e3a5f]" />
                Where You Stand Now
              </h2>
              <div className="prose max-w-none text-gray-700 space-y-4">
                <p>
                  You have finished the four-chapter chassis of this course. Chapter 1 covered the legal frame. Chapter 2 
                  covered the constitutional overlay. Chapter 3 covered the ethical spine. Chapter 4 covered the documentary 
                  architecture that turns authority into a working file. The chassis is built.
                </p>
                <p>
                  Everything from this point forward is field work — surveillance, contact, arrest, transport, extradition. 
                  Before you take a step into that half of the course, read this case all the way through. It is the record 
                  the front half was built around.
                </p>
              </div>
            </div>

            {/* A Note from the Author */}
            <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="section-note-from-author">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#1e3a5f]" />
                A Note from the Author
              </h2>
              <div className="prose max-w-none text-gray-700 space-y-4">
                <p>
                  I owned the bail company named in this record. The stand-down order on February 20 came from me. Every 
                  fact in the pages that follow is drawn from the affidavits, warrant applications, interrogation reports, 
                  and body camera footage in the Mesa County file.
                </p>
                <p>
                  Every violation in the case maps to a doctrine you already studied in Chapters 1 through 4. Read the 
                  record. Identify the failures against the chassis. That is the work of this module.
                </p>
              </div>
            </div>

            {/* The Short Version */}
            <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="section-short-version">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1e3a5f]" />
                The Short Version
              </h2>
              <div className="prose max-w-none text-gray-700 space-y-3">
                <p>
                  A supplemental agent, fewer than sixty days on the roster and with no Recovery Contract on the Madrid 
                  bond, entered two residences over two days. The first was the defendant's own residence in Delta, opened 
                  on the landlord's verbal permission. The second was the defendant's brother's apartment in Grand Junction, 
                  opened by a maintenance worker's master key. Both entries occurred in defiance of a direct stand-down order.
                </p>
                <p>
                  Inside the second apartment, with a child present, the agent pointed a less-lethal launcher across the 
                  room, fired one round when the defendant produced a replica handgun, and drove the defendant out through 
                  a second-story balcony. The agent was charged. His charges were dismissed. The tenant whose home was 
                  entered was never compensated. The doctrinal failures behind that outcome are what the rest of this 
                  module examines.
                </p>
              </div>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                To the Student
              </h2>
              <div className="prose max-w-none text-gray-700 space-y-4">
                <p>
                  This document is the foundation of this course. It is not a supplemental reading. It is not an example. 
                  <strong> It is the case this course was built around.</strong>
                </p>
                <p>
                  Every law covered in these ten chapters, every statute cited, every professional standard taught — each one exists because 
                  situations like this one happen. When you read about written authorization requirements in Chapter 4, this case is why. 
                  When you read about third-party residence entry in Chapter 2, this case is why. When you read about ego-driven conduct 
                  in Chapter 3, color of law in Chapter 5, investigation standards in Chapter 6 — this case is why.
                </p>
                <p className="bg-amber-50 border-l-4 border-amber-500 p-4 text-amber-900">
                  <strong>All facts in this case study are drawn from the public record</strong> of Mesa County Combined Courts Case No. 2022CR599: 
                  arrest warrant affidavit, felony summons request, search warrant affidavits, interrogation reports, property seizure forms, 
                  and body camera descriptions compiled by Detective Brad Schuman, Grand Junction Police Department.
                </p>
              </div>
            </div>

            {/* The Parties */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                The Parties Involved
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <h3 className="font-bold text-blue-900">Jose Daniel Madrid — Defendant on Bond</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Had utilized the bail company to secure release from custody on a $15,000 bond. Subsequently failed to appear, 
                    accumulating ten active warrants and eight restraining orders, four of which prohibited firearm possession.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-red-500">
                  <h3 className="font-bold text-red-900">Ken Hill & Megan Snell — Supplemental Agents</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Had been working for the bail company in a limited supplemental capacity for <strong>fewer than sixty days</strong>. 
                    Their tasks were limited to observation support, paper service, and backup presence. Hill had <strong>no written 
                    authorization</strong> to independently conduct the Madrid recovery.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-500">
                  <h3 className="font-bold text-green-900">Fernando Madrid — Third-Party Victim</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Jose Madrid's brother. Resident of apartment 213, 200 Railyard Loop. <strong>Not a party to any bail bond agreement</strong> 
                    and had no legal obligation to cooperate with any enforcement action. His son was present during the incident.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-500">
                  <h3 className="font-bold text-amber-900">Jason Gallegos — Maintenance Worker</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Employee of the Railyard Loop apartment complex. His sequence of decisions on February 21, 2022 formed 
                    <strong> the chain of access that enabled the unlawful entry</strong> into Fernando Madrid's apartment.
                  </p>
                </div>
              </div>
            </div>

            {/* Timeline of Events */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Chronology of Events
              </h2>
              
              {/* Early 2022 - Montrose - Hill's First Assignment */}
              <div className="border-l-4 border-gray-300 pl-6 mb-8">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  ~One month before February 2022 — Montrose, CO
                </div>
                <h3 className="font-bold text-lg mb-2">Hill's First Assignment — The Cosigner Summons</h3>
                <p className="text-gray-700 mb-3">
                  Kenneth Hill's first assignment with the bail company was to serve the <strong>cosigner</strong> on the
                  Madrid bond with a civil summons — a lawsuit filed to indemnify the surety. It was a paperwork task,
                  narrowly scoped, with no arrest, contact, or enforcement component.
                </p>
                <p className="text-gray-700 mb-3">
                  Hill <strong>threatened the cosigner with jail</strong> at the door and refused to leave the property.
                  The cosigner contacted law enforcement. Hill later reported the encounter to the bondsman,
                  characterizing himself as the victim of a racial slur. <strong>Hill did not disclose that he had threatened
                  the cosigner prior to the confrontation.</strong>
                </p>
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                  <strong className="text-red-800">Chapter 3 Reference — Ego-Driven Conduct:</strong>
                  <p className="text-red-700 mt-1">
                    Hill was assigned a routine task: serve civil process. He escalated it into a confrontation, then misrepresented 
                    what happened to the bondsman. An agent who believes his own judgment supersedes the authorization structure 
                    is not demonstrating confidence — he is demonstrating that he does not understand what his authority actually is.
                  </p>
                  <p className="text-red-700 mt-2 italic">
                    <strong>Note:</strong> The bail company only learned the truth of this encounter months later,
                    during the investigation into the events of February 20-21. At the time, Hill's account was accepted at face value.
                  </p>
                </div>
              </div>

              {/* Jan 18 - Delta Informant Intel */}
              <div className="border-l-4 border-gray-300 pl-6 mb-8">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  January 18, 2022 — Delta, CO
                </div>
                <h3 className="font-bold text-lg mb-2">Informant Intelligence</h3>
                <p className="text-gray-700 mb-3">
                  The bondsman received informant intelligence identifying <strong>1426 Silver Street, Delta, Colorado</strong>
                  as the residence Jose Madrid was operating out of. On arrival at the address, the bail company observed
                  Madrid leaving the residence in his truck.
                </p>
                <p className="text-gray-700 mb-3">
                  The company opened a surveillance file on the address and worked it through drive-by observation for
                  approximately one week. <strong>The truck did not return to the address during that period.</strong>
                </p>
              </div>

              {/* Feb 19 - The Park Meeting */}
              <div className="border-l-4 border-blue-400 pl-6 mb-8">
                <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
                  <Clock className="w-4 h-4" />
                  February 19, 2022 — Delta area park (evening)
                </div>
                <h3 className="font-bold text-lg mb-2">The Park Meeting</h3>
                <p className="text-gray-700 mb-3">
                  Kenneth Hill had joined the bail company approximately one month earlier. He lived near the Delta area.
                  On the evening of February 19, Hill contacted the bondsman because he knew from company communications
                  that the address was being worked. <strong>The bondsman had not met Hill in person before that evening.</strong>
                  The two arranged to meet at a park near 1426 Silver Street.
                </p>
                <p className="text-gray-700 mb-3">At that meeting, the bondsman:</p>
                <ul className="list-disc list-inside text-gray-700 mb-3 ml-4">
                  <li>Confirmed to Hill that the address was under active surveillance</li>
                  <li>Instructed Hill that his role, if any, was <strong>drive-by observation only</strong> — note whether Madrid's truck was present and report back</li>
                  <li>Made no assignment authorizing contact, approach, entry, or interaction with any occupant of the property</li>
                </ul>
                <p className="text-gray-700 mb-3">Hill agreed to the observation-only role.</p>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <strong className="text-blue-800">Chapter 4 Reference — Authorization Is Specific:</strong>
                  <p className="text-blue-700 mt-1">
                    Under the three-tier documentary architecture, Tier 1 authorization is bond-specific <em>and</em> task-specific.
                    Hill's authorization on the Madrid file, as of the park meeting, was drive-by observation only. Nothing in
                    that authorization permitted contact, approach, forced entry, or any of the conduct that followed the next day.
                  </p>
                </div>
              </div>

              {/* Feb 20 - Delta - Corrected */}
              <div className="border-l-4 border-amber-400 pl-6 mb-8">
                <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  February 20, 2022 — Delta, CO
                </div>
                <h3 className="font-bold text-lg mb-2">Hill Acts Outside His Authorization</h3>
                <p className="text-gray-700 mb-3">
                  On the morning of February 20, Hill drove to 1426 Silver Street in full tactical gear.
                  Somehow — the public record does not fully clarify how — Hill was able to get the <strong>landlord</strong> of
                  the property to travel to the residence and meet him at the door. On her arrival, the landlord gave
                  Hill verbal permission to enter. She then departed. <strong>She was not present during the entry.</strong>
                </p>
                <p className="text-gray-700 mb-3">
                  Hill later called the bondsman and reported that the landlord had let him in to search the residence.
                  Hill said the defendant was not there, that he was going to close the door, and that the truck was in the garage.
                  The bondsman challenged Hill directly: <em>"Why would you do that? You ruined the address."</em> Hill's answer
                  was that the landlord had let him in.
                </p>
                <p className="text-gray-700 mb-3">
                  The bondsman instructed Hill to hold at the location until <strong>the assigned agent</strong> arrived.
                  The assigned agent placed a <strong>GPS tracker under Madrid's truck</strong> — the single operational
                  asset the case now had.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm mb-3">
                  <strong className="text-amber-800">Legal Analysis — The Delta Entry:</strong>
                  <p className="text-amber-700 mt-1">
                    Hill's authorization on February 19 permitted <strong>drive-by observation only.</strong> It did not
                    permit contact, approach, or entry. Even setting the authorization gap aside, the landlord's verbal
                    permission covered the threshold only. Her consent did NOT authorize forced entry, destruction of
                    property, removal of property, or any impression of law-enforcement partnership.
                    This is the moment in the record where Hill's inexperience becomes visible — he operated on the
                    industry's inherited culture (a license and tactical gear equal authority) and outside every specific
                    instruction the bondsman had given him twelve hours earlier.
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                  <strong className="text-red-800">A Note on What Actually Happened Inside:</strong>
                  <p className="text-red-700 mt-1">
                    The bondsman had only Hill's phone account at the time. Jose Madrid's later interrogation testimony —
                    forced entry through the back door, an armed search with weapons drawn, a <em>"shoot on sight"</em> directive
                    to Snell, and the theft of binoculars and a knife — did not surface until the investigation began.
                    The full record is treated in the Investigation tab.
                  </p>
                </div>
              </div>

              {/* Stand-Down + Vegas */}
              <div className="border-l-4 border-blue-400 pl-6 mb-8">
                <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
                  <Clock className="w-4 h-4" />
                  February 20, 2022 (afternoon) — The Stand-Down Order
                </div>
                <h3 className="font-bold text-lg mb-2">Bondsman Issues Stand-Down and Departs for Las Vegas</h3>
                <p className="text-gray-700 mb-3">
                  Following the Delta entry and the GPS placement, the bondsman departed Colorado for a personal trip to
                  Las Vegas. Before departing, he issued a clear working instruction to the entire team:
                  <em> "the GPS is on the truck — stand down through the weekend, we will pick it up when I'm back."</em>
                  Every member of the team was told to hold position. The operation was paused.
                </p>
                <p className="text-gray-700 font-semibold text-red-700">
                  Hill was on that team. Hill heard the stand-down.
                </p>
              </div>

              {/* Feb 20 Night - GPS Moves */}
              <div className="border-l-4 border-orange-500 pl-6 mb-8">
                <div className="flex items-center gap-2 text-orange-600 text-sm mb-2">
                  <Clock className="w-4 h-4" />
                  February 20, 2022 (night) — Las Vegas / Colorado
                </div>
                <h3 className="font-bold text-lg mb-2">The GPS Moves</h3>
                <p className="text-gray-700 mb-3">
                  Late on the night of February 20, the bondsman saw on the tracker application that the GPS had begun
                  moving. He group-texted the team: <em>"the address is not ruined, the vehicle is moving."</em>
                </p>
                <p className="text-gray-700 mb-3">
                  The bondsman attempted to coordinate with <strong>Colorado State Patrol</strong> and the
                  <strong> Delta Police Department</strong> to intercept the vehicle. It was moving too fast out of
                  the area to set up an intercept.
                </p>
                <p className="text-gray-700 mb-3">
                  At approximately <strong>4:00 a.m. on February 21</strong>, the vehicle settled at an address in
                  Grand Junction known to be the residence of Madrid's brother, <strong>Fernando Madrid</strong>.
                </p>
                <p className="text-gray-700">
                  The bondsman's plan on his return was to coordinate a controlled apprehension with the Grand Junction
                  street-crimes unit. He returned to sleep.
                </p>
              </div>

              {/* Feb 21 - Grand Junction */}
              <div className="border-l-4 border-red-500 pl-6 mb-8">
                <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  February 21, 2022 — 200 Railyard Loop, Grand Junction
                </div>
                <h3 className="font-bold text-lg mb-2">The Unlawful Entry</h3>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">1. The Property Management Office</h4>
                    <p className="text-gray-700">
                      Hill entered the property management office and told staff <strong>he had arrest warrants for Jose Madrid</strong>. 
                      He had no arrest warrants. <strong>Arrest warrants are issued by courts to law enforcement officers. No bail enforcement 
                      agent in Colorado is ever issued an arrest warrant.</strong>
                    </p>
                    <p className="text-gray-700 mt-2">
                      All three staff members who interacted with Hill believed he was a law enforcement officer. The combination of 
                      full tactical gear, law enforcement-style equipment, and the claim of arrest warrants created that impression.
                    </p>
                  </div>

                  <div className="bg-amber-50 rounded p-4 border border-amber-200">
                    <h4 className="font-semibold text-amber-900 mb-2">2. Maintenance Worker Jason Gallegos</h4>
                    <p className="text-gray-700">
                      Gallegos spoke with Hill and offered to assist. <strong>At the time, Hill did not know which building or which apartment 
                      Madrid's brother occupied.</strong>
                    </p>
                    <p className="text-gray-700 mt-2">
                      Gallegos went to the property manager, obtained Fernando Madrid's apartment number and building location, 
                      and disclosed that information to Hill.
                    </p>
                    <div className="mt-3 p-3 bg-white border-l-4 border-red-500">
                      <strong className="text-red-800">VIOLATION 1:</strong> Unauthorized disclosure of tenant information. 
                      Without this disclosure, Hill did not know which building or which door. <strong>This was the first link 
                      in the chain that led to the shooting.</strong>
                    </div>
                  </div>

                  <div className="bg-amber-50 rounded p-4 border border-amber-200">
                    <h4 className="font-semibold text-amber-900 mb-2">3. Opening the Secured Building</h4>
                    <p className="text-gray-700">
                      Gallegos escorted Hill and Snell to building two and <strong>used his employee access credentials to open the 
                      secured building entrance</strong>.
                    </p>
                    <div className="mt-3 p-3 bg-white border-l-4 border-red-500">
                      <strong className="text-red-800">VIOLATION 2:</strong> Employee access credentials exist for building maintenance 
                      and staff functions. Gallegos used those credentials to admit two armed private citizens — whose authority 
                      he had no means to verify — into a locked residential building.
                    </div>
                  </div>

                  <div className="bg-red-50 rounded p-4 border border-red-200">
                    <h4 className="font-semibold text-red-900 mb-2">4. Opening a Private Residence</h4>
                    <p className="text-gray-700">
                      At the door to apartment 213, Gallegos asked Hill: <strong>"Do you want me to open it for you?"</strong> Hill said yes. 
                      <strong>Gallegos used his master key to unlock the door to Fernando Madrid's private residence.</strong>
                    </p>
                    <p className="text-gray-700 mt-2">
                      Neither Hill nor Snell knocked. Neither announced themselves. Neither identified their purpose.
                    </p>
                    <div className="mt-3 p-3 bg-white border-l-4 border-red-500">
                      <strong className="text-red-800">VIOLATION 3:</strong> A maintenance master key has one lawful function: 
                      building maintenance. There is no maintenance purpose that authorizes using that key to open a tenant's 
                      private residence for third parties without the tenant's knowledge, consent, or any emergency justification.
                    </div>
                  </div>

                  <div className="bg-red-100 rounded p-4 border-2 border-red-400">
                    <h4 className="font-semibold text-red-900 mb-2">5. Inside Apartment 213 — The Confrontation</h4>
                    <p className="text-gray-700">
                      Jose Madrid had showered and was seated at the kitchen island when the door opened. Hill entered first 
                      with a less lethal weapon pointed at Madrid and ordered him to put his hands up and get on the ground. 
                      Snell followed and remained near the doorway. <strong>Neither identified themselves.</strong>
                    </p>
                    <p className="text-gray-700 mt-2 bg-red-200 p-2 rounded">
                      <strong>Body camera footage captured at the moment of entry shows a child present in the apartment</strong>, 
                      visible to the right of the doorway as Hill and Snell entered. The child is Fernando Madrid's son. 
                      The child is not referenced in the police report.
                    </p>
                    <p className="text-gray-700 mt-2">
                      Madrid stood up, said something in Spanish, and produced what appeared to be a silver or chrome-colored handgun. 
                      Hill and Snell immediately retreated down the hallway. Fernando Madrid grabbed Jose and pulled him back toward the apartment. 
                      <strong>As Fernando pulled Jose back, Hill fired one shot.</strong> The round struck the wall near where Gallegos was standing.
                    </p>
                    <p className="text-gray-700 mt-2">
                      Jose Madrid went back inside the apartment and <strong>jumped from the balcony</strong>. He fled the complex on foot.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Legal Outcome */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Legal Proceedings and Outcomes
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left font-bold">Party</th>
                      <th className="p-3 text-left font-bold">Outcome</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="bg-red-50">
                      <td className="p-3 font-semibold">Ken Hill</td>
                      <td className="p-3">Charges dismissed with prejudice. Colorado Supreme Court agreed conduct was unlawful and he acted under "color of law." No remedy available after dismissal.</td>
                    </tr>
                    <tr className="bg-red-50">
                      <td className="p-3 font-semibold">Megan Snell</td>
                      <td className="p-3">Charges dismissed with prejudice.</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="p-3 font-semibold">Jason Gallegos</td>
                      <td className="p-3">No criminal charges filed. Filed civil action against the bail company — voluntarily dismissed after receiving analysis of his own statutory exposure.</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="p-3 font-semibold">Fernando Madrid (Victim)</td>
                      <td className="p-3 text-red-700 font-semibold">Never compensated. His home was entered without consent. A shot was fired in his hallway. His son was present. <strong>The apartment manager later sued HIM for property damage. He ended up with the bill.</strong></td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold">Jose Madrid</td>
                      <td className="p-3">Arrested. Charged with weapons and protection order violations. Original bond forfeiture was never recovered.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 bg-gray-900 text-white rounded-lg">
                <h3 className="font-bold text-lg mb-2">A Note on This Outcome</h3>
                <p className="text-gray-300">
                  Every person in this case who held authority — a badge, a bond, a master key, a property manager's office — 
                  either exceeded that authority, failed to exercise it correctly, or used it in a way that harmed someone 
                  who had nothing to do with any of it.
                </p>
                <p className="text-white font-semibold mt-3">
                  Not one of them was convicted.
                </p>
                <p className="text-gray-300 mt-3">
                  Fernando Madrid was not a defendant. He was not a party to any bond. He did not consent to the entry of his home. 
                  He did not invite any of these people to his door. His son was present. A shot was fired in his hallway.
                </p>
                <p className="text-amber-400 font-bold mt-3">
                  He was the only one who ended up with a bill.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setActiveTab('video')} className="bg-[#1e3a5f]">
                Continue to Evidence & Interrogations
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* VIDEO & INTERROGATIONS TAB */}
          <TabsContent value="video" className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Video className="w-5 h-5" />
                Video and Audio Evidence
              </h2>
              <p className="text-gray-600 mb-6">
                This case study is supported by recorded evidence which authenticates the facts stated in the case narrative.
                Where the written record and the video record differ, the video record controls.
              </p>
              
              {/* Body Cam Section */}
              <div className="mb-8">
                <h3 className="font-bold text-lg mb-4">Body Camera Footage — Ken Hill</h3>
                <BodyCamPlayer 
                  videoId={BODY_CAM_VIDEO_ID}
                  title="Body Camera Footage - Ken Hill"
                  caseInfo="February 21, 2022 | Railyard Loop Apartments, Grand Junction, CO"
                />
                <p className="text-sm text-gray-500 mt-3 text-center italic">
                  This footage shows the violations discussed in the case study. Watch for the key moments marked in the timeline.
                </p>
              </div>

              {/* Interrogation List */}
              <div className="mt-8">
                <h3 className="font-bold text-lg mb-4">Interrogation Recordings</h3>
                <p className="text-gray-600 mb-4">
                  The following interrogation recordings are available for this case study. 
                  They reveal inconsistencies between Hill's statements and the documented evidence.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4 bg-red-50">
                    <h4 className="font-semibold text-red-900">Ken Hill Interrogation</h4>
                    <p className="text-sm text-gray-600 mt-2">
                      Grand Junction Police Department, February 21, 2022. His statements contradicted body camera footage, 
                      Snell's statements, and documented facts.
                    </p>
                    <div className="mt-3 aspect-video rounded overflow-hidden border border-red-200 bg-black">
                      <iframe
                        className="w-full h-full"
                        src="https://www.youtube.com/embed/E_baNX_tnSI?rel=0"
                        title="Ken Hill Interrogation"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="mt-3 p-2 bg-white rounded text-xs">
                      <strong>Key False Statements:</strong>
                      <ul className="list-disc list-inside mt-1 text-gray-600">
                        <li>"I had arrest warrants" — No bail agent is ever issued arrest warrants</li>
                        <li>"I've worked there for ten years" — Had known bondsman fewer than 60 days</li>
                        <li>"Madrid followed us into the hallway" — Video shows Madrid went back inside</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-red-50">
                    <h4 className="font-semibold text-red-900">Megan Snell Interrogation</h4>
                    <p className="text-sm text-gray-600 mt-2">
                      Grand Junction Police Department, February 21, 2022. Her statements contradicted Hill's and the documented record.
                    </p>
                    <div className="mt-3 aspect-video rounded overflow-hidden border border-red-200 bg-black">
                      <iframe
                        className="w-full h-full"
                        src="https://www.youtube.com/embed/xUYZzw8KKo0?rel=0"
                        title="Megan Snell Interrogation"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="mt-3 p-2 bg-white rounded text-xs">
                      <strong>Key Contradiction:</strong>
                      <p className="text-gray-600 mt-1">
                        Stated they work for "themselves — Colorado Fugitive Recovery" and that Hill had known the bondsman for fewer than 60 days.
                      </p>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-amber-50">
                    <h4 className="font-semibold text-amber-900">Jason Gallegos Interrogation</h4>
                    <p className="text-sm text-gray-600 mt-2">
                      Grand Junction Police Department, February 21, 2022. Gave inconsistent accounts of how he obtained the apartment number.
                    </p>
                    <div className="mt-3 aspect-video rounded overflow-hidden border border-amber-200 bg-black">
                      <iframe
                        className="w-full h-full"
                        src="https://www.youtube.com/embed/P2Cdrbg49Yg?rel=0"
                        title="Jason Gallegos Interrogation"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="mt-3 p-2 bg-white rounded text-xs">
                      <strong>Key Admission (2:57:59):</strong>
                      <p className="text-gray-600 mt-1">
                        Initially claimed Hill asked for a specific unit. Later admitted Hill had not known the apartment number — 
                        Gallegos had obtained it from the office manager and provided it to Hill.
                      </p>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h4 className="font-semibold text-blue-900">Jose Madrid Interview</h4>
                    <p className="text-sm text-gray-600 mt-2">
                      Montrose County Jail, March 29, 2022. Confirmed producing an object and stated it was a poor decision.
                    </p>
                    <div className="mt-3 aspect-video rounded overflow-hidden border border-blue-200 bg-black">
                      <iframe
                        className="w-full h-full"
                        src="https://www.youtube.com/embed/LQPfPmsv8X0?rel=0"
                        title="Jose Madrid Interview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="mt-3 p-2 bg-white rounded text-xs">
                      <strong>Inconsistent Description:</strong>
                      <p className="text-gray-600 mt-1">
                        Initially described object as a BB gun, later as a spring-loaded airsoft pistol with an orange tip. 
                        Cell phone video shows silver/chrome handgun with no visible orange tip.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setActiveTab('story')}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Full Story
              </Button>
              <Button onClick={() => setActiveTab('investigation')} className="bg-[#1e3a5f]">
                Continue to Investigation
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* INVESTIGATION TAB */}
          <TabsContent value="investigation" className="space-y-6" data-testid="tabcontent-investigation">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Gavel className="w-5 h-5" />
                The Surety's Investigation
              </h2>
              <p className="text-gray-600 mb-6">
                After the shooting, the bail company opened its own investigation. What follows is not a summary of the 
                criminal case — it is the surety's parallel record of what the interrogations, the civil filing, and the 
                Division of Insurance disclosure actually established.
              </p>

              {/* Interrogation Analysis */}
              <div className="mb-8">
                <h3 className="font-bold text-lg mb-3 text-[#1e3a5f]">1. Interrogation Analysis</h3>
                <p className="text-gray-700 mb-4">
                  Grand Junction Police Department interrogated Hill, Snell, and Gallegos on February 21, 2022. Each account 
                  contradicted the others. The body camera footage controlled every dispute.
                </p>

                <div className="space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-500 rounded p-4">
                    <h4 className="font-semibold text-red-900 mb-2">Hill's Interrogation — Documented False Statements</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li><strong>"I had arrest warrants."</strong> No bail enforcement agent in Colorado is ever issued an arrest warrant. This statement was made to three property staff members and to the officer taking his statement.</li>
                      <li><strong>"I've worked for the bondsman for ten years."</strong> Hill had been on the roster fewer than sixty days.</li>
                      <li><strong>"Madrid followed us out into the hallway."</strong> Body camera footage shows Fernando pulling Jose back into the apartment before the shot was fired.</li>
                      <li><strong>Omission:</strong> The child visible in body camera footage at the moment of entry was not mentioned in Hill's statement or in the police report drafted from it.</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 border-l-4 border-red-500 rounded p-4">
                    <h4 className="font-semibold text-red-900 mb-2">Snell's Interrogation — Contradictions with Hill</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li>Identified the operating entity as "Colorado Fugitive Recovery — we work for ourselves," not the bail company on the Madrid bond.</li>
                      <li>Confirmed Hill's tenure on the file was fewer than sixty days — directly contradicting Hill's ten-year claim.</li>
                      <li>Placed herself at the doorway, not in the apartment, which is consistent with the body cam but inconsistent with Hill's narrative of a joint dynamic entry.</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-4">
                    <h4 className="font-semibold text-amber-900 mb-2">Gallegos's Interrogation — The 2:57:59 Correction</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li><strong>Version 1:</strong> Hill approached him and asked to be let into apartment 213 by number.</li>
                      <li><strong>Version 2 (at 2:57:59):</strong> Hill did not know the apartment number. Gallegos obtained it from the office manager, disclosed it to Hill, opened the secured building with his employee credentials, and used his master key on the private door.</li>
                      <li>The corrected version is the version the office-manager record and building-access logs later validated. It is also the version that establishes Gallegos's complicity in Second-Degree Burglary and First-Degree Criminal Trespass on his own testimony.</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Jose Madrid Interview — Montrose County Jail, March 29, 2022</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li>Confirmed producing an object during the confrontation and characterized the decision as a poor one.</li>
                      <li>Described the object first as a BB gun, later as a spring-loaded airsoft pistol with an orange tip.</li>
                      <li>The cell phone video introduced during the criminal proceedings shows a silver or chrome handgun with no visible orange tip. The inconsistency did not change the fact governing the Castle Doctrine analysis: what Hill perceived at the moment of entry and what Jose was justified in producing in response to an armed unlawful intruder.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Response Letters */}
              <div className="mb-8">
                <h3 className="font-bold text-lg mb-3 text-[#1e3a5f]">2. The Response Letters</h3>
                <p className="text-gray-700 mb-4">
                  Two documents closed the civil exposure in this case. Neither was a settlement offer. Neither offered money. 
                  Both were doctrinal walk-throughs written on the surety's letterhead.
                </p>

                <div className="space-y-4">
                  <div className="bg-gray-50 border rounded p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Letter One — To Gallegos's Attorney</h4>
                    <p className="text-sm text-gray-700 mb-2">
                      Gallegos, at the encouragement of law enforcement, filed a civil action against Hill and the bail company 
                      alleging reckless endangerment. The response letter did four things in sequence:
                    </p>
                    <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 ml-2">
                      <li>Walked the attorney through the four-element consent framework from Chapter 2, § 2.6, and established that Gallegos's consent failed on the authority element.</li>
                      <li>Established Gallegos's status as the operative link in the entry chain on his own corrected testimony at 2:57:59.</li>
                      <li>Established Gallegos's complicity in a Class 4 felony under C.R.S. 18-1-603 and 18-4-203 on that same testimony — meaning the civil case, if it proceeded, would supply the criminal transcript.</li>
                      <li>Closed the vicarious-liability theory by producing the observation-only authorization on record, the stand-down order issued February 20, and the absence of any Recovery Contract on the Madrid bond.</li>
                    </ol>
                    <p className="text-sm text-gray-700 mt-2">
                      <strong>The attorney voluntarily dismissed the case one week after receipt.</strong> No settlement was paid.
                    </p>
                  </div>

                  <div className="bg-gray-50 border rounded p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Letter Two — To the District Attorney's Office</h4>
                    <p className="text-sm text-gray-700 mb-2">
                      A separate letter — factually parallel, functionally different — went to the Mesa County District Attorney's 
                      office. Its purpose was the discharge of the licensed producer's statutory duty under C.R.S. 10-1-127.
                    </p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li>Documented Hill's false statements to police as knowing misrepresentations made in the course of a bail-bond enforcement operation.</li>
                      <li>Documented Gallegos's contradicted testimony and civil filing built on that testimony as insurance fraud on its face under C.R.S. 10-1-128.</li>
                      <li>Attached the surety's own investigation file — authorization records, the stand-down text thread, the observation-only assignment.</li>
                    </ul>
                    <p className="text-sm text-gray-700 mt-2">
                      The letter was not a request for charging decisions. It was the record required to close the surety's file with the fraud-reporting duty fully discharged.
                    </p>
                  </div>
                </div>
              </div>

              {/* DOI Outcome */}
              <div className="mb-4">
                <h3 className="font-bold text-lg mb-3 text-[#1e3a5f]">3. The Division of Insurance Outcome</h3>
                <p className="text-gray-700 mb-4">
                  The parallel report to the Colorado Division of Insurance discharged the second half of the producer's 
                  statutory duty: report suspected insurance fraud to the regulator of the profession.
                </p>

                <div className="bg-white border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left font-bold">Party Reported</th>
                        <th className="p-3 text-left font-bold">DOI Outcome</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="p-3 font-semibold">Kenneth Hill</td>
                        <td className="p-3">Referred for review of licensed conduct. License activity ended.</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Megan Snell</td>
                        <td className="p-3">Referred for review. License activity ended.</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">The Bail Company (surety)</td>
                        <td className="p-3 text-green-700">
                          <strong>No adverse action.</strong> The DOI file reflects the observation-only authorization, the stand-down order, and the discharge of the fraud-reporting duty. The bondsman's license and the company's authority remained in continuous good standing.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 bg-[#1e3a5f] text-white rounded">
                  <h4 className="font-bold mb-2">The Standard the Investigation Established</h4>
                  <p className="text-sm text-gray-100">
                    Every doctrine on the front half of this course — legal authority, constitutional limits, ethical duty, 
                    documentary architecture — held up in the record on its own terms. The bondsman did not survive this case 
                    because of a good lawyer or a kind judge. He survived it because <strong>every instrument the chassis 
                    requires existed in writing before the operation began</strong>, and because the statutory duties attached 
                    to the license were discharged the moment the record required it. That is the standard this course is 
                    teaching you to build.
                  </p>
                </div>
              </div>

              {/* Blind Fury / We Are Not the Police */}
              <div className="mb-4">
                <h3 className="font-bold text-lg mb-3 text-[#1e3a5f]">4. Blind Fury — We Are Not the Police</h3>
                <p className="text-gray-700 mb-4">
                  There is one more lesson to take from this record, and it is one the working profession has to internalize 
                  before the field work half of this course begins.
                </p>

                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>We are not the police.</strong> The bail-suretyship license carries its own authority and its own 
                    limits, and neither one overlaps with the peace-officer authority most agents assume they are borrowing 
                    when they put on tactical gear.
                  </p>
                  <p>
                    Most law enforcement officers have no idea what a bail enforcement agent can and cannot do. That gap is 
                    not their fault — it is the working reality. When an agent shows up in the field acting like a cop, 
                    the responding officer sees a private citizen in body armor claiming authority the officer cannot verify. 
                    Add ignorance of the statute to disrespect for the limits it draws, and the officer will go out of his 
                    way to find proof that the agent acted outside his power — and charge him for it.
                  </p>
                  <p>
                    The Ken Hill case is a prime example of blind fury. Once the officers on scene decided who they were 
                    going to charge, they did not take anything else into account. Not the observation-only authorization. 
                    Not the stand-down order. Not the third-party consent framework. Not the maintenance worker's role in 
                    the entry chain. All they wanted was a conviction.
                  </p>
                  <p>
                    The professional working position is the reverse of the field posture Hill took. The agent's job is to 
                    show the responding officer exactly what the license authorizes, exactly what documentation is on file, 
                    and exactly why the action taken sits inside those limits. An agent who cannot do that in one clear 
                    paragraph — on scene, without notes — will hand the officer everything he needs to build a case that 
                    the four chapters of this course exist to prevent.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setActiveTab('video')}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Evidence
              </Button>
              <Button onClick={() => setActiveTab('questions')} className="bg-[#1e3a5f]" data-testid="continue-to-questions-btn">
                Continue to Assessment Questions
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* QUESTIONS TAB */}
          <TabsContent value="questions">
            {/* Score Banner (when submitted) */}
            {showResults && (
              <div className={`mb-8 p-6 rounded-lg border-2 ${
                score.percentage >= 75 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-amber-50 border-amber-300'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {score.percentage >= 75 ? (
                      <CheckCircle className="w-12 h-12 text-green-600" />
                    ) : (
                      <AlertCircle className="w-12 h-12 text-amber-600" />
                    )}
                    <div>
                      <h3 className="text-xl font-bold">
                        {score.percentage >= 75 ? 'Great Work!' : 'Review Needed'}
                      </h3>
                      <p className="text-gray-600">
                        You scored {score.correct} out of {score.total} ({score.percentage}%)
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-[#1e3a5f]">{score.percentage}%</div>
                    <p className="text-sm text-gray-500">75% required to pass</p>
                  </div>
                </div>
              </div>
            )}

            {/* Questions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              {showResults ? (
                /* Results View */
                <div className="space-y-8">
                  {caseStudy?.questions.map((q, idx) => {
                    const userAnswer = answers[q.number];
                    const isCorrect = userAnswer === q.correct_answer;
                    
                    return (
                      <div key={idx} className={`p-6 rounded-lg border-2 ${
                        isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-start gap-3 mb-4">
                          <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            isCorrect ? 'bg-green-600' : 'bg-red-600'
                          }`}>
                            {q.number}
                          </span>
                          <p className="font-medium text-gray-800">{q.question}</p>
                        </div>
                        
                        <div className="ml-11 space-y-2 mb-4">
                          {q.options.map(opt => (
                            <div
                              key={opt.letter}
                              className={`p-3 rounded border ${
                                opt.letter === q.correct_answer
                                  ? 'bg-green-100 border-green-300 font-medium' 
                                  : userAnswer === opt.letter && !isCorrect
                                    ? 'bg-red-100 border-red-300'
                                    : 'bg-white'
                              }`}
                            >
                              <span className="font-medium mr-2">{opt.letter}.</span>
                              {opt.text}
                              {opt.letter === q.correct_answer && <span className="ml-2 text-green-700 font-semibold">✓ Correct</span>}
                              {userAnswer === opt.letter && opt.letter !== q.correct_answer && <span className="ml-2 text-red-700">✗ Your answer</span>}
                            </div>
                          ))}
                        </div>
                        
                        <div className="ml-11 p-4 bg-white rounded border">
                          <p className="text-sm font-medium text-gray-700 mb-1">Discussion:</p>
                          <p className="text-gray-600">{q.explanation}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Quiz Questions */
                <div>
                  <div className="text-center mb-8">
                    <h2 className="text-xl font-bold text-[#1e3a5f]">Answer all 10 questions based on the Ken Hill case</h2>
                    <p className="text-sm text-gray-500 mt-2">Each question addresses a specific violation from the chapters you have completed.</p>
                  </div>
                  
                  {caseStudy?.questions.map((q, idx) => (
                    <div key={idx} className="mb-8 p-6 bg-gray-50 rounded-lg" data-testid={`question-${q.number}`}>
                      <div className="flex items-start gap-3 mb-4">
                        <span className="flex-shrink-0 w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-bold">
                          {q.number}
                        </span>
                        <p className="font-medium text-gray-800">{q.question}</p>
                      </div>
                      
                      <RadioGroup 
                        value={answers[q.number] || ''} 
                        onValueChange={(value) => handleAnswer(q.number, value)}
                        className="ml-11 space-y-2"
                      >
                        {q.options.map(opt => (
                          <div key={opt.letter} className="flex items-center space-x-3 p-3 bg-white rounded border hover:border-[#1e3a5f] transition-colors">
                            <RadioGroupItem value={opt.letter} id={`q${q.number}-${opt.letter}`} />
                            <Label htmlFor={`q${q.number}-${opt.letter}`} className="cursor-pointer flex-1">
                              <span className="font-medium mr-2">{opt.letter}.</span>
                              {opt.text}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setActiveTab('investigation')}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Investigation
              </Button>
              
              {!showResults ? (
                <Button
                  onClick={handleSubmit}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                  data-testid="submit-case-study-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Submit Case Study
                </Button>
              ) : (
                <div className="flex gap-4">
                  {score.percentage < 75 && (
                    <Button
                      variant="outline"
                      onClick={handleRetry}
                    >
                      Retry Case Study
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate('/final-exam')}
                    className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Continue to Final Exam
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default KenHillCaseStudyPage;
